import React, { useMemo, useCallback, useState } from 'react';
import { Box, Wrap } from '@televet/kibble-ui/build/chakra';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { Swatch } from '@televet/kibble-ui/build/components/Swatch';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Text } from '@televet/kibble-ui/build/components/Text';
import partial from 'lodash-es/partial';
import { ChannelListFilterDraftValueOption } from '../types/ChannelListFilterDraft';
import { IFilterOption } from '../interfaces/IFilterOption';
import { ChannelListFilterRowLabel } from '../types/ChannelListFilterRowLabel';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface ChannelListFilterRowBaseProps {
  selectedOptions: ChannelListFilterDraftValueOption[];
  options: IFilterOption[];
  isReadOnly: boolean;
  isLastRow?: boolean;
  showSelectAllOption?: boolean;
  onValueChange: (value: ChannelListFilterDraftValueOption[]) => void;
}

interface ChannelListFilterRowConversationStatusProps extends ChannelListFilterRowBaseProps {
  label: ChannelListFilterRowLabel.ConversationStatus;
  showArchived: boolean;
  onShowArchivedToggle: () => void;
}

interface ChannelListFilterRowNonConversationStatusProps extends ChannelListFilterRowBaseProps {
  label: Exclude<ChannelListFilterRowLabel, ChannelListFilterRowLabel.ConversationStatus>;
  showArchived?: never;
  onShowArchivedToggle?: never;
}

type ChannelListFilterRowProps =
  | ChannelListFilterRowNonConversationStatusProps
  | ChannelListFilterRowConversationStatusProps;

const ChannelListFilterRow = ({
  label,
  selectedOptions,
  showSelectAllOption = true,
  options,
  isReadOnly,
  isLastRow,
  onValueChange,
}: ChannelListFilterRowProps): JSX.Element => {
  const { trackEvent } = useTracking();

  const handleToggleOption = useCallback(
    (option: ChannelListFilterDraftValueOption): void => {
      const existingIndex = selectedOptions.findIndex(({ value }) => value === option.value);
      const newValue = selectedOptions.slice();
      if (existingIndex > -1) {
        newValue.splice(existingIndex, 1);
        trackEvent(ConversationEvents.FilterRemoved, { filterType: label });
      } else {
        newValue.push(option);
        trackEvent(ConversationEvents.FilterApplied, { filterType: label });
      }
      onValueChange(newValue);
    },
    [selectedOptions, onValueChange, trackEvent, label],
  );

  const handleSelection = useCallback(
    (selectedItem: MenuItemProps) => {
      const label = typeof selectedItem.label === 'string' ? selectedItem.label : '';

      if (!selectedItem.isSelected) {
        const values = selectedOptions.filter((option) => option.value !== selectedItem.value);
        onValueChange(values);
      } else {
        onValueChange([...selectedOptions, { label: label, value: selectedItem.value || '', color: '' }]);
      }
    },
    [onValueChange, selectedOptions],
  );

  const [searchText, setSearchText] = useState('');

  const transformedOptions: MenuItemProps[] = useMemo(() => {
    return options.reduce<MenuItemProps[]>((acc, option) => {
      const { label, value, color } = option;

      if (Array.isArray(value)) {
        // For grouped options, create a MenuItemProp for each item in the array
        const groupItems = value.map((item) => ({
          label: item.label,
          value: String(item.value),
          groupLabel: label,
          ...(item.color && { leftElement: <Swatch bgColor={item.color} size="sm" borderRadius="3px" /> }),
          isSelected: selectedOptions.some((option) => option.value === item.value),
        }));
        return [...acc, ...groupItems];
      }

      // For regular options, create a single MenuItemProp
      return [
        ...acc,
        {
          label,
          value: String(value),
          ...(color && { leftElement: <Swatch bgColor={color} size="sm" borderRadius="3px" /> }),
          isSelected: selectedOptions.some((option) => option.value === value),
        },
      ];
    }, []);
  }, [options, selectedOptions]);

  const filteredOptions = useMemo(() => {
    if (!searchText) return transformedOptions;

    return transformedOptions.filter((option) => {
      const { label } = option;
      return !!searchText && label?.toString().toLowerCase().includes(searchText.toLowerCase());
    });
  }, [searchText, transformedOptions]);

  const handleGroupSelection = useCallback(
    (selectedItems: MenuItemProps[]): void => {
      const filteredOptionValues = new Set(filteredOptions.map((option) => option.value));
      const preservedOptions = selectedOptions.filter((option) => !filteredOptionValues.has(option.value));
      const newSelectedItems = selectedItems.map((item) => {
        const label = typeof item.label === 'string' ? item.label : '';
        return { label, value: item.value || '', color: '' };
      });
      const values = [...preservedOptions, ...newSelectedItems];
      onValueChange(values);
    },
    [onValueChange, selectedOptions, filteredOptions],
  );

  return (
    <Box borderBottom="1px" borderBottomColor={isLastRow ? 'transparent' : 'border.default'}>
      <Wrap width="400px" minWidth="400px" spacing="1.5" py="3" px="4" align="center">
        <Text fontWeight="bold" size="sm">
          {label}:
        </Text>
        {!isReadOnly && (
          <Menu
            buttonProps={{ leftIconName: 'plus', size: 'xs', variant: 'tertiary', showRightIcon: false }}
            listProps={{
              menuItems: filteredOptions,
              isMultiSelect: true,
              isSearchable: true,
              onSearchCallback: (e): void => setSearchText(e.target.value),
              canSelectGroups: showSelectAllOption,
              isGrouped: true,
              onSelectAll: handleGroupSelection,
              onSelect: (selectedItem): void => {
                handleSelection(selectedItem);
              },
            }}
          />
        )}
        {selectedOptions.map((option) => {
          const { value, label, color } = option;
          return (
            <Tag
              key={value.toString()}
              color="text.default"
              bg="background.alpha"
              size="sm"
              {...(color && { leftElement: <Swatch w={3} h={3} borderRadius="xs" bgColor={color} /> })}
              label={label}
              closeButtonDisplay={isReadOnly ? 'never' : 'always'}
              onClose={partial(handleToggleOption, option)}
            />
          );
        })}
      </Wrap>
    </Box>
  );
};

export default ChannelListFilterRow;
